import { useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Cog, Menu, X, ArrowUpRight } from 'lucide-react';
import { useLang } from '../i18n';
import { EASE } from './motion';

const Navbar = () => {
  const { lang, setLang, t } = useLang();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const links = [
    { to: '/azienda', label: t('nav.azienda') },
    { to: '/catalogo', label: t('nav.catalogo') },
    { to: '/private-label', label: t('nav.pl') },
    { to: '/contatti', label: t('nav.contatti') },
  ];

  const LangSwitch = ({ dark = false }) => (
    <div className="flex items-center border border-current/30 text-xs font-display font-bold" data-testid="lang-switcher">
      {['it', 'en'].map((l) => (
        <button
          key={l}
          data-testid={`lang-${l}-button`}
          onClick={() => setLang(l)}
          className={`px-2.5 py-1.5 uppercase tracking-widest transition-colors duration-300 ${
            lang === l ? (dark ? 'bg-white text-[#22303C]' : 'bg-primary text-primary-foreground') : 'opacity-50 hover:opacity-100'
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );

  return (
    <>
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-background/70 border-b border-foreground/10" data-testid="main-navbar">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 h-16 md:h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group" data-testid="logo-link">
            <img src="/images/propaq-icon.png" alt="PROPAQ" className="w-9 h-9 rounded-md transition-transform duration-500 group-hover:scale-105" />
            <span className="flex flex-col leading-none">
              <span className="font-display font-extrabold text-xl tracking-tight">PRO<span className="text-secondary">PAQ</span></span>
              <span className="font-display text-[8px] uppercase tracking-[0.32em] text-foreground/50 mt-1">Produzione Alimentare</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.to.slice(1)}-link`}
                className={({ isActive }) =>
                  `text-sm font-medium tracking-wide transition-colors duration-300 hover:text-secondary ${isActive ? 'text-secondary' : 'text-foreground/70'}`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-5">
            <LangSwitch />
            <Link
              to="/private-label"
              data-testid="nav-b2b-cta"
              className="group flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 text-sm font-display font-bold uppercase tracking-wider hover:bg-secondary transition-colors duration-300"
            >
              {t('nav.b2b')}
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>

          <button
            className="lg:hidden p-2"
            onClick={() => setOpen(true)}
            data-testid="mobile-menu-open-button"
            aria-label={t('nav.menu')}
          >
            <Menu className="w-6 h-6" />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[60] bg-[#22303C] text-[#F7F5F0] flex flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: EASE }}
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-5 h-16 border-b border-white/10">
              <span className="font-display font-extrabold text-xl">PRO<span className="text-secondary">PAQ</span></span>
              <button onClick={() => setOpen(false)} data-testid="mobile-menu-close-button" aria-label={t('nav.close')}>
                <X className="w-6 h-6" />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-2">
              {[{ to: '/', label: t('nav.home') }, ...links].map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ y: 40, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.1 + i * 0.07, duration: 0.6, ease: EASE }}
                >
                  <NavLink
                    to={l.to}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-${l.to === '/' ? 'home' : l.to.slice(1)}-link`}
                    className={`block font-display font-extrabold text-4xl py-2 tracking-tight ${location.pathname === l.to ? 'text-secondary' : ''}`}
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <div className="px-8 pb-10">
              <LangSwitch dark />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
